import { useMemo, useState } from 'react';
import type { LeaveApplication, LeaveStatus, LeaveType } from '@chronos/shared';
import { useAuth } from '@/auth/AuthContext';
import { useLeaveApplications, useLeaveBank, useLeaveMutations } from '@/hooks/queries';
import { Card, ConfirmButton, EmptyState, ErrorNote, PageHeader, Spinner, SubmitButton } from '@/components/ui';
import { formatDate, formatDateTime, toDateInput } from '@/lib/format';
import { currentMonthKey } from '@/pages/AttendancePage';

const STATUS: Record<LeaveStatus, { label: string; cls: string }> = {
  PENDING: { label: 'Awaiting approval', cls: 'bg-amber-100 text-amber-800' },
  APPROVED: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-800' },
  REJECTED: { label: 'Rejected', cls: 'bg-rose-100 text-rose-800' },
  CANCELLED: { label: 'Cancelled', cls: 'bg-slate-200 text-slate-600' },
};

export function StatusBadge({ status }: { status: LeaveStatus }) {
  return <span className={`badge ${STATUS[status].cls}`}>{STATUS[status].label}</span>;
}

export function TypeBadge({ type }: { type: LeaveType }) {
  return <span className={`badge ${type === 'MEDICAL' ? 'bg-sky-100 text-sky-800' : 'bg-violet-100 text-violet-800'}`}>{type === 'MEDICAL' ? 'Medical' : 'Casual'}</span>;
}

/** Marks leave an admin recorded for the employee rather than the employee filing it. */
export function FiledByBadge({ name }: { name: string | null }) {
  if (!name) return null;
  return <span className="badge bg-amber-100 text-amber-800" title={`Filed on this employee's behalf by ${name}`}>Filed by admin</span>;
}

/** Employee-facing: apply, and see the outcome. */
export function LeavePage() {
  const { user, settings } = useAuth();
  const notice = settings?.casualLeaveNoticeWorkingDays ?? 0;
  const autoMedical = settings?.autoApproveMedicalLeave ?? true;
  const noticeText = notice === 0 ? 'by the previous working day' : `with ${notice} working day${notice === 1 ? '' : 's'}' notice`;
  const { data, isLoading, error } = useLeaveApplications();
  const bank = useLeaveBank(currentMonthKey());
  const { apply, cancel } = useLeaveMutations();

  const tomorrow = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return toDateInput(d);
  }, []);
  const today = useMemo(() => toDateInput(new Date()), []);

  const [type, setType] = useState<LeaveType>('CASUAL');
  const [from, setFrom] = useState(tomorrow);
  const [to, setTo] = useState(tomorrow);
  const [halfDay, setHalfDay] = useState(false);
  const [reason, setReason] = useState('');

  const switchType = (t: LeaveType) => {
    setType(t);
    // Casual cannot be for today; medical usually is.
    if (t === 'MEDICAL') { setFrom(today); setTo(today); }
    else if (from <= today) { setFrom(tomorrow); setTo(tomorrow); }
  };

  const open = (data ?? []).filter((a) => a.status === 'PENDING' || (a.status === 'APPROVED' && a.toDate >= today));
  const history = (data ?? []).filter((a) => !open.includes(a));

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Apply for leave"
        description={`Casual leave needs approval and must be requested ${noticeText}. Medical leave ${autoMedical ? 'is approved immediately' : 'also goes to an admin, but can be filed the same day'}.`}
      />

      <div className="grid gap-4 lg:grid-cols-5">
        <Card className="p-5 lg:col-span-3">
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              apply.mutate({ type, from, to: halfDay ? from : to, halfDay, reason: reason.trim() }, { onSuccess: () => setReason('') });
            }}
          >
            <div className="flex gap-2">
              {(['CASUAL', 'MEDICAL'] as LeaveType[]).map((t) => (
                <button
                  type="button"
                  key={t}
                  onClick={() => switchType(t)}
                  className={`flex-1 rounded-lg border px-3 py-2.5 text-left transition ${type === t ? 'border-brand-500 bg-brand-50' : 'border-slate-300 hover:bg-slate-50'}`}
                >
                  <p className="text-sm font-medium text-slate-900">{t === 'CASUAL' ? 'Casual leave' : 'Medical leave'}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {t === 'CASUAL' ? `Apply ${noticeText} · admin approves` : `Same day is fine · ${autoMedical ? 'approved automatically' : 'admin approves'}`}
                  </p>
                </button>
              ))}
            </div>

            <ErrorNote error={apply.error} />

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="label" htmlFor="lv-from">From</label>
                <input id="lv-from" type="date" className="input" required min={type === 'CASUAL' ? tomorrow : undefined} value={from} onChange={(e) => { setFrom(e.target.value); if (to < e.target.value) setTo(e.target.value); }} />
              </div>
              <div>
                <label className="label" htmlFor="lv-to">To</label>
                <input id="lv-to" type="date" className="input" required min={from} value={halfDay ? from : to} disabled={halfDay} onChange={(e) => setTo(e.target.value)} />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" className="h-4 w-4 rounded border-slate-300" checked={halfDay} onChange={(e) => setHalfDay(e.target.checked)} />
              Half day <span className="text-xs text-slate-500">(single date, counts as 0.5)</span>
            </label>

            <div>
              <label className="label" htmlFor="lv-reason">Reason</label>
              <textarea id="lv-reason" className="input min-h-[72px]" required minLength={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={type === 'MEDICAL' ? 'e.g. Fever, doctor advised rest' : 'e.g. Family function'} />
            </div>

            {type === 'CASUAL' && from <= today && (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Casual leave for today or earlier will be refused — the day has already started. If you are unwell, apply for medical leave instead.
              </p>
            )}
            {type === 'CASUAL' && from > today && (
              <p className="text-xs text-slate-500">Apply {noticeText} — weekends and holidays don't count. For leave on a Monday that means by {notice === 0 ? 'Friday' : notice === 1 ? 'Thursday' : `${notice + 1} working days before`}.</p>
            )}

            <SubmitButton pending={apply.isPending} className="btn-primary">{type === 'MEDICAL' ? 'Submit medical leave' : 'Request casual leave'}</SubmitButton>
          </form>
        </Card>

        <Card className="p-5 lg:col-span-2">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Paid leave available</p>
          {bank.data ? (
            <>
              <p className="text-3xl font-semibold tabular-nums text-brand-700">{bank.data.available} <span className="text-base font-normal text-slate-500">day{bank.data.available === 1 ? '' : 's'}</span></p>
              <p className="mt-2 text-xs text-slate-500">
                Approved leave is paid from this bank. Absence without an approved application is deducted from salary even if days remain.
              </p>
            </>
          ) : (
            <Spinner className="mt-2 h-4 w-4" />
          )}
          <dl className="mt-4 space-y-1.5 border-t border-slate-100 pt-3 text-xs text-slate-600">
            <div className="flex justify-between"><dt>Casual leave</dt><dd>{noticeText} · needs approval</dd></div>
            <div className="flex justify-between"><dt>Medical leave</dt><dd>same day OK · {autoMedical ? 'auto-approved' : 'needs approval'}</dd></div>
            <div className="flex justify-between"><dt>Cancel</dt><dd>while pending, or before an approved leave starts</dd></div>
          </dl>
        </Card>
      </div>

      <ErrorNote error={error ?? cancel.error} />

      <Section title="Open requests" items={open} isLoading={isLoading} empty="Nothing pending or upcoming." onCancel={(a) => cancel.mutate(a.id)} today={today} isSelf={!!user} />
      <Section title="History" items={history} isLoading={isLoading} empty="No past applications." today={today} isSelf={!!user} />
    </div>
  );
}

function Section({ title, items, isLoading, empty, onCancel, today }: { title: string; items: LeaveApplication[]; isLoading: boolean; empty: string; onCancel?: (a: LeaveApplication) => void; today: string; isSelf: boolean }) {
  return (
    <Card className="mt-4">
      <div className="border-b border-slate-200 px-4 py-3"><h2 className="text-sm font-semibold text-slate-800">{title}</h2></div>
      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : items.length === 0 ? (
        <EmptyState title={empty} />
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((a) => {
            const canCancel = onCancel && (a.status === 'PENDING' || (a.status === 'APPROVED' && a.fromDate > today));
            return (
              <li key={a.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <TypeBadge type={a.type} />
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-sm text-slate-800">
                    <span>
                      {formatDate(`${a.fromDate}T00:00:00`)}{a.toDate !== a.fromDate ? ` – ${formatDate(`${a.toDate}T00:00:00`)}` : ''}
                      <span className="ml-2 text-xs text-slate-500">{a.units} day{a.units === 1 ? '' : 's'}{a.halfDay ? ' (half)' : ''}</span>
                    </span>
                    <FiledByBadge name={a.filedByName} />
                  </p>
                  <p className="truncate text-xs text-slate-500">{a.reason}</p>
                  {a.decisionNote && a.status !== 'APPROVED' && <p className="text-xs text-rose-700">“{a.decisionNote}” — {a.decidedByName ?? 'admin'}</p>}
            {a.status === 'PENDING' && a.type === 'MEDICAL' && <p className="text-xs text-slate-500">Medical leave waits for an admin here.</p>}
                </div>
                <div className="text-right">
                  <StatusBadge status={a.status} />
                  <p className="mt-0.5 text-[11px] text-slate-400">{a.decidedAt ? `${formatDateTime(a.decidedAt)}` : `applied ${formatDateTime(a.createdAt)}`}</p>
                </div>
                {canCancel && <ConfirmButton className="btn-ghost px-2 py-1 text-xs text-rose-600" label="Cancel" confirmLabel="Confirm" onConfirm={() => onCancel(a)} />}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
